import React, { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getRegionCoordinates,
  calculateMapBounds,
  getPerformanceColor,
  getSequentialColor,
  UK_MAP_CENTER,
  UK_MAP_DEFAULT_ZOOM,
  ColorScaleType
} from '../../utils/ukRegionCoordinates';
import './MapVisualization.css';

export interface MapDataPoint {
  region?: string;          // Display name (used for label and PMA fallback lookup)
  location?: string;        // Alternative to region (for query_kpi_analysis output)
  name?: string;            // Alternative to region
  value: number;            // The metric value
  lat?: number;             // Optional: direct latitude (if provided, skips PMA lookup)
  lng?: number;             // Optional: direct longitude (if provided, skips PMA lookup)
  latitude?: number;        // Alternative to lat (for query_kpi_analysis output)
  longitude?: number;       // Alternative to lng (for query_kpi_analysis output)
  label?: string;           // Optional: custom label
  details?: string;         // Optional: tooltip/popup details
}

export interface MapVisualizationProps {
  title?: string;
  data: MapDataPoint[];
  mapType?: 'markers' | 'heatmap';  // Visualization type (default: markers)
  colorScale?: ColorScaleType | 'performance' | 'sequential';
  valueLabel?: string;
  showLegend?: boolean;
  height?: number;
  markerSize?: 'fixed' | 'scaled';  // Whether marker size reflects value (default: scaled)
}

const MapVisualization: React.FC<MapVisualizationProps> = ({
  title,
  data,
  mapType = 'markers',
  colorScale = 'performance',
  valueLabel = 'Value',
  showLegend = true,
  height = 400,
  markerSize = 'scaled'
}) => {
  console.log('🗺️ [MAP DEBUG] MapVisualization rendered with:', {
    title,
    dataType: typeof data,
    isArray: Array.isArray(data),
    dataLength: Array.isArray(data) ? data.length : 'N/A',
    colorScale,
    valueLabel
  });

  // Safely handle invalid data - must be before hooks but we use a flag instead of early return
  const isValidData = data && Array.isArray(data);

  // Helper to get coordinates for a point (direct or PMA lookup)
  const getPointCoordinates = (point: MapDataPoint): { lat: number; lng: number; displayName: string } | null => {
    // Get lat/lng from either format (lat/lng or latitude/longitude)
    const lat = point.lat ?? point.latitude;
    const lng = point.lng ?? point.longitude;
    
    // If direct coordinates provided, use them
    if (typeof lat === 'number' && typeof lng === 'number') {
      // Use label, location, region, name - whatever is available
      const name = point.label || point.location || point.region || point.name || 'Location';
      return { lat, lng, displayName: name };
    }
    
    // Otherwise, try PMA lookup using region
    const regionName = point.region || point.location || point.name;
    if (regionName) {
      const coords = getRegionCoordinates(regionName);
      return coords;
    }
    
    return null;
  };

  // Calculate min/max for color scaling
  const { min, max, total, validData } = useMemo(() => {
    if (!isValidData) {
      return { min: 0, max: 0, total: 0, validData: [] };
    }
    // Filter to points with valid value AND either coords or valid region
    const validPoints = data.filter(d => {
      if (!d || typeof d.value !== 'number' || isNaN(d.value)) return false;
      
      // Check for coordinates in either format (lat/lng or latitude/longitude)
      const lat = d.lat ?? d.latitude;
      const lng = d.lng ?? d.longitude;
      if (typeof lat === 'number' && typeof lng === 'number') return true;
      
      // Or has valid PMA region (non-empty string that maps to coords)
      const regionName = d.region || d.location || d.name;
      if (typeof regionName === 'string' && regionName.length > 0) {
        return getRegionCoordinates(regionName) !== null;
      }
      return false;
    });
    const values = validPoints.map(d => d.value);
    return {
      min: values.length > 0 ? Math.min(...values) : 0,
      max: values.length > 0 ? Math.max(...values) : 0,
      total: values.reduce((a, b) => a + b, 0),
      validData: validPoints
    };
  }, [data, isValidData]);

  // Calculate map bounds based on data points
  const { center, zoom } = useMemo(() => {
    // If we have direct coordinates, calculate bounds from them
    const pointsWithDirectCoords = validData.filter(d => typeof d.lat === 'number' && typeof d.lng === 'number');
    
    if (pointsWithDirectCoords.length > 0) {
      const lats = pointsWithDirectCoords.map(d => d.lat!);
      const lngs = pointsWithDirectCoords.map(d => d.lng!);
      
      // Also include any PMA-lookup points
      validData.forEach(d => {
        if (typeof d.lat !== 'number' || typeof d.lng !== 'number') {
          const regionName = d.region || d.location || d.name || '';
          if (regionName) {
            const coords = getRegionCoordinates(regionName);
            if (coords) {
              lats.push(coords.lat);
              lngs.push(coords.lng);
            }
          }
        }
      });
      
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      
      // Calculate zoom based on spread
      const latSpread = Math.max(...lats) - Math.min(...lats);
      const lngSpread = Math.max(...lngs) - Math.min(...lngs);
      const maxSpread = Math.max(latSpread, lngSpread);
      
      let zoom = 6;
      if (maxSpread < 1) zoom = 9;
      else if (maxSpread < 3) zoom = 7;
      else if (maxSpread < 5) zoom = 6;
      else zoom = 5;
      
      return { center: [centerLat, centerLng] as [number, number], zoom };
    }
    
    // Fall back to PMA-based bounds
    const regions = validData
      .map(d => d.region || d.location || d.name || '')
      .filter(r => r.length > 0);
    return calculateMapBounds(regions);
  }, [validData]);

  // Early return AFTER hooks
  if (!isValidData) {
    console.error('🗺️ [MAP DEBUG] Invalid data prop', data);
    return (
      <div className="map-visualization map-no-data">
        <p>Invalid map data provided.</p>
      </div>
    );
  }

  // Get color for a value
  const getColor = (value: number): string => {
    if (colorScale === 'sequential') {
      return getSequentialColor(value, min, max);
    }
    return getPerformanceColor(value, min, max);
  };

  // Calculate circle radius based on value (relative sizing)
  const getRadius = (value: number): number => {
    if (markerSize === 'fixed') return 15;
    if (max === min) return 20;
    const ratio = (value - min) / (max - min);
    return 12 + ratio * 18; // 12-30 range
  };

  // Format number with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  // Calculate rank for a region
  const getRank = (value: number): number => {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    return sorted.findIndex(d => d.value === value) + 1;
  };

  if (validData.length === 0) {
    return (
      <div className="map-visualization map-no-data">
        <p>No valid region data to display on map.</p>
        <p className="map-hint">Regions provided: {data.map(d => d.region).join(', ')}</p>
      </div>
    );
  }

  return (
    <div className="map-visualization">
      {title && <h3 className="map-title">{title}</h3>}
      
      <div className="map-container" style={{ height }}>
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom={false}
          className="leaflet-map"
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          
          {validData.map((point, index) => {
            // Use direct coords if provided, otherwise fall back to PMA lookup
            const coords = getPointCoordinates(point);
            if (!coords) return null;
            
            const color = getColor(point.value);
            const radius = getRadius(point.value);
            const rank = getRank(point.value);
            const percentage = total > 0 ? ((point.value / total) * 100).toFixed(1) : '0';
            
            return (
              <CircleMarker
                key={`${point.region}-${index}`}
                center={[coords.lat, coords.lng]}
                radius={radius}
                fillColor={color}
                fillOpacity={0.8}
                color="#fff"
                weight={2}
                opacity={1}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={0.95} permanent={false}>
                  {coords.displayName}
                </Tooltip>
                <Popup>
                  <div className="map-popup">
                    <h4 className="popup-title">📍 {String(coords.displayName)}</h4>
                    <div className="popup-divider"></div>
                    <div className="popup-stats">
                      <div className="popup-stat">
                        <span className="popup-label">{String(valueLabel)}:</span>
                        <span className="popup-value">{formatNumber(point.value)}</span>
                      </div>
                      <div className="popup-stat">
                        <span className="popup-label">Rank:</span>
                        <span className="popup-value">#{rank} of {validData.length}</span>
                      </div>
                      <div className="popup-stat">
                        <span className="popup-label">Share:</span>
                        <span className="popup-value">{percentage}%</span>
                      </div>
                      {point.details && (
                        <div className="popup-stat popup-details">
                          <span className="popup-label">Details:</span>
                          <span className="popup-value">{typeof point.details === 'string' ? point.details : JSON.stringify(point.details)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {showLegend && (
        <div className="map-legend">
          <div className="legend-gradient">
            <div className="legend-bar" style={{
              background: colorScale === 'sequential' 
                ? 'linear-gradient(to right, #dbeafe, #3b82f6, #1d4ed8)'
                : 'linear-gradient(to right, #ef4444, #eab308, #22c55e)'
            }}></div>
            <div className="legend-labels">
              <span>Low ({formatNumber(min)})</span>
              <span>{valueLabel}</span>
              <span>High ({formatNumber(max)})</span>
            </div>
          </div>
          <div className="legend-size">
            <span className="legend-size-label">Circle size = relative value</span>
          </div>
        </div>
      )}

      <div className="map-summary">
        <span className="summary-item">
          <strong>{validData.length}</strong> regions
        </span>
        <span className="summary-divider">•</span>
        <span className="summary-item">
          <strong>{formatNumber(total)}</strong> total {valueLabel.toLowerCase()}
        </span>
        <span className="summary-divider">•</span>
        <span className="summary-item">
          Avg: <strong>{formatNumber(Math.round(total / validData.length))}</strong>
        </span>
      </div>
    </div>
  );
};

export default MapVisualization;

